// src/services/uploadService.js
/* eslint-disable no-undef */
import { apiClient } from "../utils/apiClient"; // axios instance
import { API as API_URLS } from "../utils/apiUrls";
import { v4 as uuidv4 } from "uuid";

export const initProof = async (payload) => {
  const url = API_URLS.proofsInit();
  const resp = await apiClient.post(url, payload);
  return resp.data;
};

export const createClaim = async (body) => {
  const idempotencyKey = uuidv4();
  const token = localStorage.getItem("authToken");

  const payload = {
    ...body,
    idempotency_key: idempotencyKey,
  };

  const resp = await apiClient.post(API_URLS.claimUpload(), payload, {
    headers: { Authorization: token },
  });

  return resp.data;
};

export const uploadPartToPresignedUrl = async (presignedUrl, body, contentType, signal, attempt = 1) => {
  const headers = {};

  const enforceContentTypeHeader = false;
  if (enforceContentTypeHeader && contentType) {
    headers["Content-Type"] = contentType;
  }

const res = await fetch(presignedUrl, {
  method: "PUT",
  // no custom headers
  body,
  signal,
  mode: "cors",
});

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`Upload part failed with status ${res.status} ${res.statusText} ${text}`);
    err.status = res.status;
    throw err;
  }

  const etagRaw = res.headers.get("ETag") || res.headers.get("etag") || "";
  return etagRaw.replace(/^"|"$/g, "");
};

export const uploadFileWithParts = async ({ file, parts, partSize = null, concurrency = 3, signal = null, onProgress = () => { }, maxRetries = 3 }) => {
  if (!file) throw new Error("file is required");
  if (!Array.isArray(parts) || parts.length === 0) throw new Error("parts list is empty");

  const totalBytes = file.size;
  let uploadedBytes = 0;
  const uploadedParts = [];

  const getSliceForPart = (partNumber) => {
    if (!partSize) {
      return { start: 0, end: file.size };
    }
    const start = (partNumber - 1) * partSize;
    const end = Math.min(start + partSize, file.size);
    return { start, end };
  };

  const queue = parts.slice();
  const workerCount = Math.max(1, Math.min(concurrency, queue.length));
  const workers = new Array(workerCount).fill(null).map(async () => {
    while (queue.length > 0) {
      const part = queue.shift();

      // normalize fields using nullish coalescing safely
      const partNumberRaw = part.part_number ?? part.partNumber ?? part.part ?? null;
      const presignedUrl = part.presigned_url ?? part.presignedUrl ?? part.presigned ?? null;

      if (!partNumberRaw || !presignedUrl) {
        throw new Error("Invalid part descriptor from init response");
      }

      const partNumber = Number(partNumberRaw);
      const { start, end } = getSliceForPart(partNumber);
      const blob = file.slice(start, end);
      const chunkSize = end - start;

      let attempt = 0;
      let lastError = null;
      while (attempt <= maxRetries) {
        attempt += 1;
        try {
          const etag = await uploadPartToPresignedUrl(presignedUrl, blob, file.type || "application/octet-stream", signal, attempt);
          uploadedParts.push({ part_number: partNumber, etag, size: chunkSize });
          uploadedBytes += chunkSize;
          onProgress(Math.round((uploadedBytes / totalBytes) * 100));
          break;
        } catch (err) {
          lastError = err;
          if (attempt > maxRetries) {
            throw new Error(`Failed to upload part ${partNumber}: ${err.message || err}`);
          }
          // backoff
          await new Promise((r) => setTimeout(r, 200 * attempt));
        }
      }
    }
  });

  await Promise.all(workers);

  // sort parts in ascending order
  uploadedParts.sort((a, b) => a.part_number - b.part_number);
  onProgress(100);
  return uploadedParts;
};

/**
 * completeMultipartOnServer - POST /v1/pocas/uploads/complete
 * server expects snake_case: { merchant_id, upload_id, bucket, key, parts: [{part_number, etag, size}] }
 */
export const completeMultipartOnServer = async ({ merchantId, uploadId, bucket, key, parts }) => {
  const payload = {
    merchant_id: merchantId,
    upload_id: uploadId,
    bucket,
    key,
    parts: parts.map(p => ({ part_number: Number(p.part_number ?? p.partNumber), etag: p.etag, size: p.size ?? null })),
  };

  const url = API_URLS.uploadsComplete();
  const resp = await apiClient.post(url, payload);
  return resp.data;
};

/**
 * completeProof - POST /v1/pocas/proofs/{proofId}/completed?merchantId=...
 */
export const completeProof = async (proofId, merchantId) => {
  const url = API_URLS.proofsCompleted(proofId);
  const resp = await apiClient.post(url, null, { params: { merchantId } });
  return resp.data;
};

export const attachProofToClaim = async (proofId, claimId, merchantId) => {
  const url = API_URLS.proofsAttachToClaim(proofId);
  const resp = await apiClient.post(url, null, { params: { claimId, merchantId } });
  return resp.data;
};

export const uploadFileAndComplete = async ({ file, initResp, merchantId, claimId = null, signal = null, onProgress = () => { }, concurrency = 3, maxRetries = 3 }) => {
  // initResp may be the raw init object or a wrapper { data: ... }
  const init = initResp?.data ?? initResp;

  const proofId = init?.proof_id ?? init?.proofId;
  const uploadId = init?.upload_id ?? init?.uploadId;
  const bucket = init?.bucket;
  const key = init?.key;
  const parts = init?.parts ?? init?.parts_list ?? init?.partsArray;

  if (!proofId || !uploadId || !bucket || !key || !Array.isArray(parts) || parts.length === 0) {
    throw new Error("Invalid init response (missing proof_id/upload_id/bucket/key/parts)");
  }

  // upload parts and collect ETags
  const uploadedParts = await uploadFileWithParts({
    file,
    parts,
    partSize: init.part_size ?? init.partSize ?? null,
    concurrency,
    signal,
    onProgress,
    maxRetries,
  });

  const completeResp = await completeMultipartOnServer({
    merchantId,
    uploadId,
    bucket,
    key,
    parts: uploadedParts,
  });

  const proofCompleteResp = await completeProof(proofId, merchantId);

  if (claimId) {
    try {
      await attachProofToClaim(proofId, claimId, merchantId);
    } catch (err) {
      console.warn("attachProofToClaim failed:", err);
    }
  }

  return {
    proofId,
    uploadCompleteResponse: completeResp,
    proofCompleteResponse: proofCompleteResp,
  };
};

export const abortUpload = async ({ uploadId, merchantId }) => {
  if (!uploadId) throw new Error("uploadId is required to abort upload");
  const url = API.abortUpload(uploadId);
  const resp = await apiClient.post(url, null, { params: { merchantId } });
  return resp.data;
};

export const abortClaimUploads = async ({ merchantId, claimId }) => {
  if (!claimId) throw new Error("claimId is required");
  const url = `${process.env.REACT_APP_POCAS_BASE_URL}/v1/pocas/uploads/abort-claim`;
  const resp = await apiClient.post(url, null, { params: { merchantId, claimId } });
  return resp.data;
};